/**
 * Currency-aware conversion between decimal money amounts and integer minor
 * units (the Agentic Logic contract's ONE money representation — see
 * ig-types functions/agentic-logic conventions).
 *
 * ISO-4217 defines a minor-unit exponent per currency: most use 2 (USD cents),
 * some use 0 (JPY — the yen IS the minor unit), a few use 3 (KWD fils).
 * Shopify's APIs hand us DECIMAL amounts as strings (`"41.50"`), so the
 * conversion parses digits rather than multiplying floats — `41.5 * 100`
 * in IEEE-754 land is not guaranteed to be 4150.
 *
 * Rounding: inputs with more fractional digits than the currency's exponent
 * are rounded HALF-UP on the absolute value (0.005 USD -> 1 cent,
 * -0.005 USD -> -1 cent). Well-formed platform amounts never need this; it
 * only guards float artifacts and over-precise merchant input.
 */

/** ISO-4217 currencies with 0 minor-unit digits. */
const ZERO_DECIMAL_CURRENCIES = new Set([
  "BIF",
  "CLP",
  "DJF",
  "GNF",
  "ISK",
  "JPY",
  "KMF",
  "KRW",
  "PYG",
  "RWF",
  "UGX",
  "UYI",
  "VND",
  "VUV",
  "XAF",
  "XOF",
  "XPF",
]);

/** ISO-4217 currencies with 3 minor-unit digits. */
const THREE_DECIMAL_CURRENCIES = new Set([
  "BHD",
  "IQD",
  "JOD",
  "KWD",
  "LYD",
  "OMR",
  "TND",
]);

/** ISO-4217 fund currencies with 4 minor-unit digits. */
const FOUR_DECIMAL_CURRENCIES = new Set(["CLF", "UYW"]);

/**
 * Minor-unit digits for an ISO-4217 currency code (case-insensitive).
 * Unknown codes get the ISO default of 2.
 */
export function currencyMinorUnitDigits(currency: string): number {
  const code = currency.toUpperCase();
  if (ZERO_DECIMAL_CURRENCIES.has(code)) {
    return 0;
  }
  if (THREE_DECIMAL_CURRENCIES.has(code)) {
    return 3;
  }
  if (FOUR_DECIMAL_CURRENCIES.has(code)) {
    return 4;
  }
  return 2;
}

const DECIMAL_AMOUNT_RE = /^(-?)(\d+)(?:\.(\d+))?$/;

/** Expands JavaScript's canonical number text without introducing a rounding step. */
function numberToPlainDecimal(amount: number): string {
  const text = String(amount);
  const exponentIndex = text.search(/e/i);
  if (exponentIndex === -1) {
    return text;
  }

  const coefficient = text.slice(0, exponentIndex);
  const exponent = Number(text.slice(exponentIndex + 1));
  const sign = coefficient.startsWith("-") ? "-" : "";
  const unsignedCoefficient = sign ? coefficient.slice(1) : coefficient;
  const [whole, fraction = ""] = unsignedCoefficient.split(".");
  const coefficientDigits = `${whole}${fraction}`;
  const decimalIndex = whole.length + exponent;

  if (decimalIndex <= 0) {
    return `${sign}0.${"0".repeat(-decimalIndex)}${coefficientDigits}`;
  }
  if (decimalIndex >= coefficientDigits.length) {
    return `${sign}${coefficientDigits}${"0".repeat(decimalIndex - coefficientDigits.length)}`;
  }
  return `${sign}${coefficientDigits.slice(0, decimalIndex)}.${coefficientDigits.slice(decimalIndex)}`;
}

/**
 * Converts a decimal money amount to integer minor units of `currency`
 * ("41.50" USD -> 4150; "1000" JPY -> 1000; "1.250" KWD -> 1250).
 *
 * String inputs are parsed digit-wise (never via float math). Number inputs
 * are stringified first; non-finite numbers and unparseable strings throw —
 * money is never guessed at.
 */
export function toMinorUnits(
  amount: string | number,
  currency: string
): number {
  const digits = currencyMinorUnitDigits(currency);

  let text: string;
  if (typeof amount === "number") {
    if (!Number.isFinite(amount)) {
      throw new Error(`Cannot convert non-finite amount to minor units`);
    }
    text = numberToPlainDecimal(amount);
  } else {
    text = amount.trim();
  }

  const match = DECIMAL_AMOUNT_RE.exec(text);
  if (!match) {
    throw new Error(
      `Cannot convert amount "${String(amount)}" to minor units: not a plain decimal`
    );
  }
  const [, sign, whole, fraction = ""] = match;

  const scaledFraction = fraction.slice(0, digits).padEnd(digits, "0");
  let minor = Number(whole) * 10 ** digits + Number(scaledFraction || "0");

  // Half-up rounding on the first dropped digit (absolute value).
  const firstDropped = fraction.charAt(digits);
  if (firstDropped && Number(firstDropped) >= 5) {
    minor += 1;
  }

  if (!Number.isSafeInteger(minor)) {
    throw new Error(
      `Amount "${String(amount)}" ${currency} exceeds the safe integer range in minor units`
    );
  }

  return sign === "-" ? -minor : minor;
}

/**
 * Converts integer minor units back to the decimal string the platform's
 * query syntaxes expect (4150 USD -> "41.50"; 1000 JPY -> "1000";
 * 1250 KWD -> "1.250"). The inverse of {@link toMinorUnits}.
 */
export function fromMinorUnits(minor: number, currency: string): string {
  if (!Number.isSafeInteger(minor)) {
    throw new Error(`Minor-unit amount must be a safe integer, got ${minor}`);
  }
  const digits = currencyMinorUnitDigits(currency);
  const sign = minor < 0 ? "-" : "";
  const abs = Math.abs(minor);
  if (digits === 0) {
    return `${sign}${abs}`;
  }
  const text = String(abs).padStart(digits + 1, "0");
  const whole = text.slice(0, -digits);
  const fraction = text.slice(-digits);
  return `${sign}${whole}.${fraction}`;
}
